import React, {
  createContext,
  useContext,
  useState,
  useCallback,
} from "react";
import {
  createWorkflow as apiCreateWorkflow,
  updateWorkflowById as apiUpdateWorkflow,
  deleteWorkflowById as apiDeleteWorkflow,
  loadWorkflows as apiLoadWorkflows,
  validateWorkflowChain as apiValidateWorkflowChain,
  addWorkflowToFavorites as apiAddWorkflowToFavorites,
} from "../service/apiService";
import { Workflow, WorkflowListResponse } from "../types";
import { toast } from "sonner";
import { useUser } from "./UserContext";

interface PaginationData {
  page: number;
  items_per_page: number;
  total_pages: number;
  has_next: boolean;
}

interface ErrorState {
  message: string;
  code?: number;
}

interface WorkflowContextProps {
  workflows: Workflow[];
  selectedWorkflow: Workflow | null;
  setSelectedWorkflow: (workflow: Workflow | null) => void;
  fetchWorkflows: (viewType?: string) => Promise<void>;
  createWorkflow: (workflowData: Workflow) => Promise<any>;
  updateWorkflowData: (workflowId: string, updatedData: Partial<Workflow>) => Promise<void>;
  deleteWorkflowById: (workflowId: string) => Promise<void>;
  validateWorkflow: (assistant_chain: string[]) => Promise<any>;
  toggleFavorite: (workflowId: string) => Promise<void>;
  loading: boolean;
  error: ErrorState | null;
  pagination: PaginationData;
  resetError: () => void;
  currentFilter: string;
  updateCurrentFilter: (filter: string) => void;
}

const WorkflowContext = createContext<WorkflowContextProps | undefined>(undefined);

export const useWorkflow = (): WorkflowContextProps => {
  const context = useContext(WorkflowContext);
  if (!context) {
    throw new Error("useWorkflow must be used within a WorkflowProvider");
  }
  return context;
};

interface WorkflowProviderProps {
  children: React.ReactNode;
}

export const WorkflowProvider: React.FC<WorkflowProviderProps> = ({ children }) => {
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorState | null>(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    items_per_page: 10,
    total_pages: 1,
    has_next: false,
  });

  const [currentFilter, setCurrentFilterState] = useState<string>(() => {
    try {
      return localStorage.getItem('workflow-list-filter') || 'my_workflows';
    } catch {
      return 'my_workflows';
    }
  });

  const updateCurrentFilter = useCallback((filter: string) => {
    setCurrentFilterState(filter);
    try {
      localStorage.setItem('workflow-list-filter', filter);
    } catch (error) {
      console.warn('Failed to save filter to localStorage:', error);
    }
  }, []);

  const { userId } = useUser();

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  const fetchWorkflows = useCallback(async (viewType?: string) => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const filter = viewType || currentFilter;
      const response: WorkflowListResponse = await apiLoadWorkflows(
        pagination.page,
        pagination.items_per_page,
        filter
      );

      setWorkflows(response.workflows || []);
      if (response.pagination) {
        setPagination(response.pagination);
      }
    } catch (error: any) {
      console.error("Error fetching workflows:", error);
      setError({
        message: error.message || "Failed to fetch workflows",
        code: error.response?.status,
      });
      toast.error("Failed to load workflows");
    } finally {
      setLoading(false);
    }
  }, [userId, currentFilter, pagination.page, pagination.items_per_page]);

  const createWorkflow = useCallback(async (workflowData: Workflow) => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiCreateWorkflow(
        workflowData.workflow_name,
        workflowData.description,
        workflowData.assistant_chain,
        workflowData.model_id,
        workflowData.is_public
      );

      toast.success("Workflow created successfully!");
      await fetchWorkflows();
      return result;
    } catch (error: any) {
      console.error("Error creating workflow:", error);
      setError({
        message: error.message || "Failed to create workflow",
        code: error.response?.status,
      });
      toast.error(error.response?.data?.detail || "Failed to create workflow");
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchWorkflows]);

  const updateWorkflowData = useCallback(async (
    workflowId: string,
    updatedData: Partial<Workflow>
  ) => {
    setLoading(true);
    setError(null);

    try {
      await apiUpdateWorkflow(workflowId, updatedData);
      toast.success("Workflow updated successfully!");
      await fetchWorkflows();
    } catch (error: any) {
      console.error("Error updating workflow:", error);
      setError({
        message: error.message || "Failed to update workflow",
        code: error.response?.status,
      });
      toast.error(error.response?.data?.detail || "Failed to update workflow");
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchWorkflows]);

  const deleteWorkflowById = useCallback(async (workflowId: string) => {
    setLoading(true);
    setError(null);

    try {
      await apiDeleteWorkflow(workflowId);
      toast.success("Workflow deleted successfully!");
      await fetchWorkflows();
    } catch (error: any) {
      console.error("Error deleting workflow:", error);
      setError({
        message: error.message || "Failed to delete workflow",
        code: error.response?.status,
      });
      toast.error(error.response?.data?.detail || "Failed to delete workflow");
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchWorkflows]);

  const validateWorkflow = useCallback(async (
    assistant_chain: string[]
  ) => {
    try {
      const result = await apiValidateWorkflowChain(assistant_chain);
      return result;
    } catch (error: any) {
      console.error("Error validating workflow:", error);
      throw error;
    }
  }, []);

  const toggleFavorite = useCallback(async (workflowId: string) => {
    try {
      // Find the workflow to check current favorite status
      const workflow = workflows.find(w => w.id === workflowId);
      if (!workflow) {
        throw new Error("Workflow not found");
      }

      // Check if the workflow is currently favorited by this user
      const isFavorited = workflow.favorites?.[userId] === true;

      // Toggle the favorite status
      await apiAddWorkflowToFavorites(workflowId, !isFavorited);

      // Refresh the workflows list
      await fetchWorkflows(currentFilter);

      toast.success(isFavorited ? "Removed from favorites" : "Added to favorites");
    } catch (error: any) {
      console.error("Error toggling workflow favorite:", error);
      toast.error("Failed to update favorite status");
      throw error;
    }
  }, [workflows, userId, fetchWorkflows, currentFilter]);

  const value: WorkflowContextProps = {
    workflows,
    selectedWorkflow,
    setSelectedWorkflow,
    fetchWorkflows,
    createWorkflow,
    updateWorkflowData,
    deleteWorkflowById,
    validateWorkflow,
    toggleFavorite,
    loading,
    error,
    pagination,
    resetError,
    currentFilter,
    updateCurrentFilter,
  };

  return (
    <WorkflowContext.Provider value={value}>
      {children}
    </WorkflowContext.Provider>
  );
};
